import { useState, useCallback, useMemo, useRef } from "react";
import { Card, Shop } from "@/types/v2";
import {
  SortCriteria,
  SortingState,
  MultiLevelSortConfig,
  DEFAULT_MULTI_LEVEL_SORT_CONFIG,
  DEFAULT_SORTING_STATE,
} from "../types";
import { getCardSection } from "@/lib/cards";

interface UseMultiLevelSortingProps {
  config?: Partial<MultiLevelSortConfig>;
  initialSorting?: SortingState;
  shop?: Shop;
}

export const useMultiLevelSorting = ({
  config = {},
  initialSorting = DEFAULT_SORTING_STATE,
  shop,
}: UseMultiLevelSortingProps = {}) => {
  const sortConfig: MultiLevelSortConfig = {
    ...DEFAULT_MULTI_LEVEL_SORT_CONFIG,
    ...config,
  };

  const [sortingState, setSortingState] =
    useState<SortingState>(initialSorting);

  // Performance monitoring
  const sortPerformanceRef = useRef({
    lastSortTime: 0,
    averageSortTime: 0,
    sortCount: 0,
  });

  // Cache for sorted criteria to avoid re-sorting on every render
  const sortedCriteriaCache = useRef<{
    criteria: SortCriteria[];
    hash: string;
  } | null>(null);

  // Add or update a sort criteria
  const addSort = useCallback(
    (field: keyof Card | string, ctrlKey: boolean = false) => {
      setSortingState((prevState) => {
        const existingIndex = prevState.criteria.findIndex(
          (sort) => sort.field === field
        );

        if (existingIndex >= 0) {
          // Field already exists in sort criteria
          const existingSort = prevState.criteria[existingIndex];
          const newCriteria = [...prevState.criteria];

          if (existingSort.direction === "asc") {
            // Change to descending
            newCriteria[existingIndex] = {
              ...existingSort,
              direction: "desc",
            };
          } else {
            // Remove the sort criteria
            newCriteria.splice(existingIndex, 1);
            // Reindex priorities
            newCriteria.forEach((sort, index) => {
              sort.priority = index;
            });
          }

          return {
            ...prevState,
            criteria: newCriteria,
          };
        } else {
          // Add new sort criteria
          if (ctrlKey && sortConfig.maxLevels > 1) {
            // Multi-level sort: add to existing criteria
            const newCriteria = [...prevState.criteria];

            // Check if we've reached max levels
            if (newCriteria.length >= sortConfig.maxLevels) {
              // Remove the last priority sort and add new one
              newCriteria.pop();
            }

            newCriteria.push({
              field,
              direction: "asc",
              priority: newCriteria.length,
            });

            return {
              ...prevState,
              criteria: newCriteria,
            };
          } else {
            // Single-level sort: replace all criteria
            return {
              ...prevState,
              criteria: [
                {
                  field,
                  direction: "asc",
                  priority: 0,
                },
              ],
            };
          }
        }
      });
    },
    [sortConfig.maxLevels]
  );

  // Remove a specific sort criteria
  const removeSort = useCallback((field: keyof Card | string) => {
    setSortingState((prevState) => {
      const newCriteria = prevState.criteria.filter(
        (sort) => sort.field !== field
      );
      // Reindex priorities
      newCriteria.forEach((sort, index) => {
        sort.priority = index;
      });

      return {
        ...prevState,
        criteria: newCriteria,
      };
    });
  }, []);

  // Clear all sorting
  const clearSort = useCallback(() => {
    setSortingState((prevState) => ({
      ...prevState,
      criteria: [],
    }));
  }, []);

  // Reorder sort priorities (for drag and drop)
  const reorderSort = useCallback((fromIndex: number, toIndex: number) => {
    setSortingState((prevState) => {
      const newCriteria = [...prevState.criteria];
      const [movedItem] = newCriteria.splice(fromIndex, 1);
      newCriteria.splice(toIndex, 0, movedItem);

      // Update priorities
      newCriteria.forEach((sort, index) => {
        sort.priority = index;
      });

      return {
        ...prevState,
        criteria: newCriteria,
      };
    });
  }, []);

  // Get the value for sorting from a card
  const getSortValue = useCallback(
    (card: Card, field: keyof Card | string): any => {
      // Handle card section fields using getCardSection
      if (shop && field === "primaryField") {
        return getCardSection(card, shop, "primaryField");
      }
      if (shop && field === "secondaryField") {
        return getCardSection(card, shop, "secondaryField");
      }
      if (shop && field === "tertiaryField") {
        return getCardSection(card, shop, "tertiaryField");
      }

      if (typeof field === "string" && field.includes(".")) {
        // Handle nested field access
        return field.split(".").reduce((obj, key) => obj?.[key], card as any);
      }
      return (card as any)[field];
    },
    [shop]
  );

  // Compare two cards based on a single sort criteria
  const compareCards = useCallback(
    (a: Card, b: Card, criteria: SortCriteria): number => {
      const aValue = getSortValue(a, criteria.field);
      const bValue = getSortValue(b, criteria.field);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return criteria.direction === "asc" ? 1 : -1;
      if (bValue == null) return criteria.direction === "asc" ? -1 : 1;

      // Handle different data types
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return criteria.direction === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        const comparison = aValue - bValue;
        return criteria.direction === "asc" ? comparison : -comparison;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return criteria.direction === "asc" ? comparison : -comparison;
      }

      // Fallback to string comparison
      const comparison = String(aValue).localeCompare(String(bValue));
      return criteria.direction === "asc" ? comparison : -comparison;
    },
    [getSortValue]
  );

  // Get sorted criteria with caching for better performance
  const getSortedCriteria = useCallback((): SortCriteria[] => {
    if (sortingState.criteria.length === 0) {
      return [];
    }

    const hash = JSON.stringify(
      sortingState.criteria.map((c) => ({
        field: c.field,
        direction: c.direction,
        priority: c.priority,
      }))
    );

    if (sortedCriteriaCache.current?.hash === hash) {
      return sortedCriteriaCache.current.criteria;
    }

    const sorted = [...sortingState.criteria].sort(
      (x, y) => x.priority - y.priority
    );
    sortedCriteriaCache.current = { criteria: sorted, hash };
    return sorted;
  }, [sortingState.criteria]);

  // Apply sorting to a list of cards with performance monitoring
  const sortCards = useCallback(
    (cards: Card[]): Card[] => {
      if (sortingState.criteria.length === 0) {
        return cards;
      }

      const startTime = performance.now();
      const sortedCriteria = getSortedCriteria();

      // Optimization: For very large datasets, consider using a stable sort
      const result = [...cards].sort((a, b) => {
        for (const criteria of sortedCriteria) {
          const result = compareCards(a, b, criteria);
          if (result !== 0) {
            return result;
          }
        }
        return 0;
      });

      const endTime = performance.now();
      const sortTime = endTime - startTime;

      // Update performance metrics
      const perf = sortPerformanceRef.current;
      perf.lastSortTime = sortTime;
      perf.sortCount += 1;
      perf.averageSortTime =
        (perf.averageSortTime * (perf.sortCount - 1) + sortTime) /
        perf.sortCount;

      // Log performance warnings for slow sorts
      if (sortTime > 200) {
        console.warn(
          `Multi-level sorting took ${sortTime.toFixed(2)}ms for ${cards.length} cards with ${sortedCriteria.length} criteria. Consider optimizing or implementing virtual scrolling.`
        );
      }

      // Log helpful performance info for large datasets
      if (cards.length > 1000) {
        console.info(
          `Multi-level sorting performance: ${sortTime.toFixed(2)}ms for ${cards.length} cards (avg: ${perf.averageSortTime.toFixed(2)}ms)`
        );
      }

      return result;
    },
    [sortingState.criteria, compareCards, getSortedCriteria]
  );

  // Get sorting information for UI display
  const sortInfo = useMemo(() => {
    return {
      hasSorting: sortingState.criteria.length > 0,
      sortCount: sortingState.criteria.length,
      maxLevels: sortConfig.maxLevels,
      canAddMore: sortingState.criteria.length < sortConfig.maxLevels,
      primarySort: sortingState.criteria.find((sort) => sort.priority === 0),
      performance: {
        lastSortTime: sortPerformanceRef.current.lastSortTime,
        averageSortTime: sortPerformanceRef.current.averageSortTime,
        sortCount: sortPerformanceRef.current.sortCount,
      },
    };
  }, [sortingState.criteria, sortConfig.maxLevels]);

  return {
    sortingState,
    setSortingState,
    addSort,
    removeSort,
    clearSort,
    reorderSort,
    sortCards,
    sortInfo,
    config: sortConfig,
  };
};
